import { useEffect, useState } from 'react';
import {
  configApiRef,
  IdentityApi,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { Button, Card, CardBody, Flex, Text } from '@backstage/ui';
import { RiMicrosoftFill } from '@remixicon/react';
import { ErrorNotice, LoadingNotice } from '../../components/bui/BuiSupport';
import { VerdantMark } from '../nav/VerdantMark';
import styles from './SignInPage.module.css';

import { createIdentityApi, optionalProfile } from './identityApi';

function friendlyAuthError(error: unknown): Error {
  const raw = error instanceof Error ? error.message : String(error);
  const normalized = raw.toLowerCase();
  if (
    normalized.includes('access_denied') ||
    normalized.includes('cancel') ||
    normalized.includes('closed')
  ) {
    return new Error('Sign-in was cancelled. You can try again when ready.');
  }
  if (
    normalized.includes('interaction_required') ||
    normalized.includes('login_required')
  ) {
    return new Error(
      'Your Microsoft session expired. Sign in again to continue.',
    );
  }
  if (normalized.includes('not configured')) {
    return new Error(
      'Microsoft sign-in is not available for this environment.',
    );
  }
  return new Error(
    'We could not sign you in. Try again, or contact the platform team if the problem continues.',
  );
}

function VerdantSignInPage({
  onSignInSuccess,
}: {
  onSignInSuccess(identityApi: IdentityApi): void;
}) {
  const authApi = useApi(microsoftAuthApiRef);
  const config = useApi(configApiRef);
  const [checkingSession, setCheckingSession] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<Error>();

  const finishSignIn = async (instantPopup: boolean) => {
    const response = await authApi.getBackstageIdentity(
      instantPopup ? { instantPopup: true } : { optional: true },
    );
    if (!response) return false;

    const profile = await optionalProfile(authApi);
    onSignInSuccess(createIdentityApi(authApi, response.identity, profile));
    return true;
  };

  useEffect(() => {
    let active = true;
    const url = new URL(window.location.href);
    const callbackError = url.searchParams.get('error');
    if (callbackError) setError(friendlyAuthError(callbackError));
    for (const name of ['error', 'error_description', 'error_uri']) {
      url.searchParams.delete(name);
    }
    if (url.href !== window.location.href) {
      window.history.replaceState({}, '', url);
    }

    void finishSignIn(false)
      .catch(caught => {
        if (active) {
          setError(friendlyAuthError(caught));
        }
      })
      .finally(() => {
        if (active) setCheckingSession(false);
      });
    return () => {
      active = false;
    };
    // The Backstage auth API and success callback are stable for this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async () => {
    setSigningIn(true);
    setError(undefined);
    try {
      if (!(await finishSignIn(true))) {
        throw new Error(
          'Microsoft authentication is not configured to support sign-in.',
        );
      }
    } catch (caught) {
      setError(friendlyAuthError(caught));
      setSigningIn(false);
    }
  };

  if (checkingSession) {
    return (
      <main className={styles.page}>
        <div className={styles.loading}>
          <LoadingNotice label="Checking your session…" />
        </div>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <Card className={styles.card}>
        <CardBody>
          <Flex direction="column" gap="6" align="start">
            <Flex align="center" gap="3">
              <VerdantMark size={42} />
              <div>
                <Text as="p" variant="title-small" weight="bold">
                  {config.getString('app.title')}
                </Text>
                <Text as="p" variant="body-small" color="secondary">
                  Tigo developer portal
                </Text>
              </div>
            </Flex>
            <div>
              <Text as="h1" variant="title-large" weight="bold">
                Sign in
              </Text>
              <Text as="p" variant="body-large" color="secondary">
                Use your Tigo Microsoft account to access Backstage.
              </Text>
            </div>
            {error && <ErrorNotice error={error} title="Sign-in failed" />}
            <Button
              variant="primary"
              size="medium"
              iconStart={<RiMicrosoftFill />}
              isPending={signingIn}
              isDisabled={signingIn}
              onPress={() => void signIn()}
              className={styles.signInButton}
            >
              Sign in with Microsoft
            </Button>
          </Flex>
        </CardBody>
      </Card>
    </main>
  );
}

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => VerdantSignInPage,
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [signInPage],
});
